/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { HallowAnalyticsEvent } from "@packages/analytics";
import {
  CommunityType,
  useRequestCommunitiesAdminCommunity,
} from "@packages/sdk";
import type { SidebarProps, SidebarTabProps } from "@packages/ui";
import {
  cssMediaQueryMinWidthTablet,
  InfoIcon,
  PermissionIcon,
  Sidebar,
  SidebarTab,
  spacing,
  Text,
} from "@packages/ui";
import { useParams } from "next/navigation";

const headerStyles: Interpolation = {
  paddingBottom: spacing.s,
  paddingLeft: spacing.s,
  paddingRight: spacing.s,
};

const sidebarStyles: Interpolation = {
  display: "none",
  [cssMediaQueryMinWidthTablet]: {
    display: "flex",
  },
};

type ParishAdminDashboardSettingsSidebarProps = Omit<SidebarProps, "children">;

export const ParishAdminDashboardSettingsSidebar = ({
  state,
  ...props
}: ParishAdminDashboardSettingsSidebarProps) => {
  const params = useParams<{ communityId: string }>();

  const { query: communityQuery } = useRequestCommunitiesAdminCommunity({
    id: parseInt(params.communityId),
  });

  const isNetwork = communityQuery.data?.type === CommunityType.network;

  const parishAdminSettingsBaseUrl = `/admin/${params.communityId}/settings`;

  const parishAdminSettingsTabs: (Pick<SidebarTabProps, "icon" | "message"> & {
    hidden?: boolean;
    path: string;
  })[] = [
    {
      icon: <InfoIcon />,
      path: "group-info",
      message: "community_settings_group_info",
    },
    {
      hidden: isNetwork,
      icon: <PermissionIcon />,
      path: "permissions",
      message: "community_settings_permissions",
    },
  ];

  return (
    <Sidebar state={state} css={sidebarStyles} {...props}>
      <Text type="headline" size="m" css={headerStyles}>
        Settings
      </Text>
      {parishAdminSettingsTabs
        .filter((tab) => !tab.hidden)
        .map(({ path, ...props }) => (
          <SidebarTab
            analyticsEvent={{
              event: HallowAnalyticsEvent.TappedCommunitySettings,
              properties: {
                option: path.replace("-", "_"),
              },
            }}
            href={`${parishAdminSettingsBaseUrl}/${path}`}
            key={path.toString()}
            state={state === "expanded" ? "expanded" : undefined}
            {...props}
          />
        ))}
    </Sidebar>
  );
};
